// =============================================================================
// Contexto de sesión: usuario, tenant (org+rol del JWT) y sucursal activa.
// Reemplaza el VITE_DEMO_BRANCH_ID. La sucursal activa se persiste en
// localStorage para que el cajero no la re-elija en cada arranque.
// =============================================================================
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

export interface Branch { id: string; name: string; code: string }

interface SessionCtx {
  session: Session | null;
  orgId: string | null;
  role: string | null;
  branches: Branch[];
  activeBranchId: string | null;
  setActiveBranch: (id: string) => void;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionCtx | null>(null);
const BRANCH_KEY = "ferresaas.activeBranch";

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [activeBranchId, setActive] = useState<string | null>(localStorage.getItem(BRANCH_KEY));
  const [loading, setLoading] = useState(true);

  // Suscribirse a los cambios de sesión de Supabase Auth.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setLoading(false); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Al haber sesión, cargar las sucursales del tenant (RLS las filtra).
  useEffect(() => {
    if (!session) { setBranches([]); return; }
    supabase.from("branches").select("id, name, code").eq("is_active", true).order("code")
      .then(({ data }) => {
        const list = (data ?? []) as Branch[];
        setBranches(list);
        // si no hay sucursal activa válida, tomar la primera
        setActive((cur) => (cur && list.some((b) => b.id === cur)) ? cur : (list[0]?.id ?? null));
      });
  }, [session]);

  const meta = (session?.user.app_metadata ?? {}) as { organization_id?: string; role?: string };

  const value: SessionCtx = {
    session,
    orgId: meta.organization_id ?? null,
    role: meta.role ?? null,
    branches,
    activeBranchId,
    setActiveBranch: (id) => { localStorage.setItem(BRANCH_KEY, id); setActive(id); },
    loading,
    signOut: async () => { await supabase.auth.signOut(); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSession debe usarse dentro de <SessionProvider>");
  return c;
}

/** Sucursal activa o lanza (las pantallas la requieren). */
export function useActiveBranch(): string {
  const { activeBranchId } = useSession();
  if (!activeBranchId) throw new Error("No hay sucursal activa");
  return activeBranchId;
}
